import type { RemotePrintOrder, WorksheetPrintRequest } from '@mathflat/domain/RemotePrint'
import type { WorksheetAssignee } from '@mathflat/domain/Worksheet'
import { action, computed, makeObservable, observable, reaction, toJS } from 'mobx'

import type { WorksheetExportType } from '@/contentComponents/modal/Download/type'
import { WorksheetPrintModal } from '@/contentComponents/modal/Print'
import { ArithmeticPrintModal } from '@/contentComponents/modal/Print/ArithmeticPrintModal'
import { printController } from '@/controller/print.controller'
import { worksheetController } from '@/controller/worksheet.controller'
import type { Worksheet } from '@/dto'
import { DTO, PrintPageInfo, PrintRangeOption } from '@/dto'
import commonRepo from '@/repository/common.repo'
import { commonErrorMessage, toastSuccess } from '@/utils/toast'

import globalLoaderService from '../globalLoader.service'
import modalService from '../modal.service'
import { localStorageService } from '../storage.service'
import { CommonPrintService } from './commonPrint.service'

type WorksheetPrintOrder = RemotePrintOrder | 'ignore-order'

class WorksheetPrintService extends CommonPrintService {
  printPageInfoList: PrintPageInfo[] | null = null
  studentNameList: string[] = []
  rangeOption = new PrintRangeOption()
  uiStore = new UIStore()
  order: RemotePrintOrder = 'by-date' // 인쇄 순서

  printWorksheetMap: Map<
    Worksheet['id'],
    {
      title: string
      assignedStudentWorksheetList: Pick<
        WorksheetAssignee,
        'studentId' | 'studentName' | 'studentWorksheetId'
      >[]
    }
  > = new Map()

  constructor() {
    super()
    makeObservable<
      WorksheetPrintService,
      'setRecentPrintOption' | 'fetchWorksheetDetail' | 'fetchPrintWorksheet' | 'getPage'
    >(this, {
      printPageInfoList: observable,
      studentNameList: observable,
      rangeOption: observable,
      uiStore: observable,
      order: observable,

      printWorksheetMap: observable,

      onChangePageInput: action,
      onChangeRangeOption: action,
      onSelectStudentList: action,
      fetchInitPrintList: action,
      onPrint: action,
      openWorksheetPrintModal: action,
      worksheetRangeList: computed,
      isStudentListSelectionRequired: computed,
      isPrintDisabled: computed,
      isSinglePrintTarget: computed,
      maximumPageRange: computed,

      // private
      setRecentPrintOption: action,
      fetchWorksheetDetail: action,
      fetchPrintWorksheet: action,
      getPage: action,
    })

    reaction(
      () => this.uiStore.visibleStudentOption,
      () => {
        if (this.uiStore.visibleStudentOption === 'VISIBLE') {
          this.printOption.update({ copyNumber: 1 })
        }
      },
    )
  }

  get printWorksheetList() {
    return Array.from(this.printWorksheetMap.entries()).map(
      ([id, { title, assignedStudentWorksheetList }]) => ({
        id,
        title,
        assignedStudentWorksheetList,
      }),
    )
  }

  get worksheetRangeList() {
    if (!this.printPageInfoList) return []

    return this.printPageInfoList.map(
      ({ initPage, elementPage, quickAnswerPage, answerSolutionPage, omrPage, targetId }) => {
        const obj = {
          page: this.getPage({
            initPage,
            elementPage,
            quickAnswerPage,
            answerSolutionPage,
            omrPage,
          }),
        }

        return {
          ...obj,
          pieceId: targetId,
        }
      },
    )
  }

  get isStudentListSelectionRequired() {
    return (
      this.uiStore.visibleStudentOption === 'VISIBLE' &&
      this.printWorksheetList.some(
        ({ assignedStudentWorksheetList }) => assignedStudentWorksheetList.length === 0,
      )
    )
  }

  get isPrintDisabled() {
    // 범위를 페이지 선택으로 했을 때, input 값이 없거나 최대 페이지보다 큰 경우
    // 학생 이름 표시를 선택하면서 학생이름이 없는 경우
    const isPageInputInvalid =
      this.uiStore.visibleRangeOption === 'SELECT' &&
      (!this.rangeOption.inputText.length || this.rangeOption.isOverMaxPage(this.maxPage))

    if (this.uiStore.visibleStudentOption === 'NONE') {
      return isPageInputInvalid
    }

    return this.isStudentListSelectionRequired || isPageInputInvalid
  }

  get useOrderOptionUseable() {
    // 인쇄 순서를 설정할 수 있는 경우
    return !this.isSinglePrintTarget && this.studentIdList.length > 1
  }

  get isSinglePrintTarget() {
    return this.printPageInfoList?.length === 1
  }

  get maxPage() {
    if (!this.printPageInfoList || this.printPageInfoList.length === 0) return 1

    const { elementPage, quickAnswerPage, answerSolutionPage, omrPage } = this.printPageInfoList[0]
    return Math.max(omrPage, elementPage, quickAnswerPage, answerSolutionPage)
  }

  get maximumPageRange() {
    if (!this.printPageInfoList || this.printPageInfoList.length === 0) return ''
    const { initPage } = this.printPageInfoList[0]
    return `${initPage || 1}-${this.maxPage}`
  }

  onChangePageInput(inputText: string) {
    this.rangeOption.update({
      inputText,
      problem: false,
      quickAnswer: false,
      answer: false,
      omr: false,
    })
  }

  onChangeRangeOption(rangeOption: Partial<PrintRangeOption>) {
    // inputText 변경 로직 추가
    if (!this.printPageInfoList || this.printPageInfoList.length === 0) return

    const { initPage, elementPage, quickAnswerPage, answerSolutionPage, omrPage } =
      this.printPageInfoList[0]
    this.rangeOption.update({
      ...rangeOption,
    })
    const inputText = this.getPage({
      initPage,
      elementPage,
      quickAnswerPage,
      answerSolutionPage,
      omrPage,
    })

    this.rangeOption.update({ inputText })
  }

  onSelectStudentList(data: { studentName: string; studentId: string }[]) {
    const studentIdList: string[] = []
    const studentNameList: string[] = []
    data.forEach(({ studentId, studentName }) => {
      studentIdList.push(studentId)
      studentNameList.push(studentName)
    })
    this.studentIdList = studentIdList
    this.studentNameList = studentNameList
  }

  async fetchInitPrintListByWorksheetList(
    printWorksheetList: WorksheetPrintService['printWorksheetList'],
    ignoredForDeleted?: boolean,
    type?: WorksheetExportType,
    hideStudentNameOption?: boolean,
  ) {
    try {
      const recentUiStore = localStorageService.getItem('RECENT_WORKSHEET_PRINT_UI_OPTION')

      if (recentUiStore) {
        this.uiStore.update({ ...recentUiStore })
      }
      // 이름표시 옵션이 숨겨진 진입(입학고사 등)은 localStorage 복원값이 VISIBLE 이면
      // 학생 선택을 요구해 인쇄가 영구 disabled 되므로 NONE 을 강제한다
      if (type === 'SCHOOL_TEST' || hideStudentNameOption) {
        this.uiStore.update({ useStudentOption: false, visibleStudentOption: 'NONE' })
      } else {
        this.uiStore.update({ useStudentOption: true })
      }

      this.printPageInfoList = await Promise.all(
        printWorksheetList.map(({ id }) => this.fetchWorksheetDetail(id, ignoredForDeleted)),
      )

      if (!this.printPageInfoList || this.printPageInfoList.length === 0) {
        return
      }

      const { initPage, elementPage, quickAnswerPage, answerSolutionPage, omrPage } =
        this.printPageInfoList[0]

      const inputText = this.getPage({
        initPage,
        elementPage,
        quickAnswerPage,
        answerSolutionPage,
        omrPage,
      })

      this.rangeOption.update({ inputText })
    } catch (error) {
      commonErrorMessage(error)
    }
  }

  async fetchInitPrintList(
    printTargetInfo: number[],
    studentNameList: string[],
    ignoredForDeleted?: boolean,
    type?: WorksheetExportType,
  ) {
    try {
      const recentUiStore = localStorageService.getItem('RECENT_WORKSHEET_PRINT_UI_OPTION')

      if (recentUiStore) {
        this.uiStore.update({ ...recentUiStore })
      }
      if (type === 'SCHOOL_TEST') {
        this.uiStore.update({ useStudentOption: false, visibleStudentOption: 'NONE' })
      } else {
        this.uiStore.update({ useStudentOption: true })
      }

      this.studentNameList = studentNameList
      this.printPageInfoList = await Promise.all(
        printTargetInfo.map((targetId) => this.fetchWorksheetDetail(targetId, ignoredForDeleted)),
      )

      if (!this.printPageInfoList || this.printPageInfoList.length === 0) {
        return
      }

      const { initPage, elementPage, quickAnswerPage, answerSolutionPage, omrPage } =
        this.printPageInfoList[0]

      const inputText = this.getPage({
        initPage,
        elementPage,
        quickAnswerPage,
        answerSolutionPage,
        omrPage,
      })

      this.rangeOption.update({ inputText })
    } catch (error) {
      commonErrorMessage(error)
    }
  }

  async openArithmeticPrintModal({
    printTargetIds,
    afterPrintFn,
  }: {
    printTargetIds: Worksheet['id'][]
    afterPrintFn: VoidFunction
  }) {
    try {
      await this.fetchPrintList()
      if (this.printAgent) {
        modalService.openModal(
          <ArithmeticPrintModal printTargetIds={printTargetIds} afterPrintFn={afterPrintFn} />,
        )
      }
    } catch (e) {
      this.printErrorNotice(e)
    }
  }

  async openWorksheetPrintModal({
    worksheets,
    afterPrintFn,
    ignoredForDeleted,
    type,
    disabledRangeOptions,
    description,
    showAsExamSheet,
    hideStudentNameOption,
    hideBanner,
  }: {
    worksheets: Worksheet[] | WorksheetPrintService['printWorksheetList']
    afterPrintFn: () => void
    ignoredForDeleted?: boolean
    type?: WorksheetExportType
    disabledRangeOptions?: ('OMR' | 'ANSWER' | 'QUICK_ANSWER' | 'PROBLEM')[]
    description?: string
    showAsExamSheet?: boolean
    hideStudentNameOption?: boolean
    hideBanner?: boolean
  }) {
    try {
      await this.fetchPrintList()

      const formattedWorksheets =
        Array.isArray(worksheets) && worksheets.length > 0 && 'assignList' in worksheets[0]
          ? (worksheets as Worksheet[]).map((worksheet) => ({
              id: worksheet.id,
              title: worksheet.title,
              assignedStudentWorksheetList:
                worksheet.assignedStudentWorksheetList?.map((assign) => ({
                  studentId: assign.studentId,
                  studentName: assign.studentName,
                  studentWorksheetId: assign.studentDataRelationId,
                })) ?? [],
            }))
          : (worksheets as WorksheetPrintService['printWorksheetList'])
      if (this.printAgent) {
        modalService.openModal(
          <WorksheetPrintModal
            worksheets={formattedWorksheets}
            afterPrintFn={afterPrintFn}
            ignoredForDeleted={ignoredForDeleted}
            type={type}
            disabledRangeOptions={disabledRangeOptions}
            description={description}
            showAsExamSheet={showAsExamSheet}
            hideStudentNameOption={hideStudentNameOption}
            hideBanner={hideBanner}
          />,
        )
      }
    } catch (error) {
      this.printErrorNotice(error)
    }
  }

  async onPrintWorksheet(
    printWorksheetList: WorksheetPrintService['printWorksheetList'],
    type?: WorksheetExportType,
    opt?: { order: WorksheetPrintOrder },
  ) {
    try {
      globalLoaderService.openLoader()
      await this.fetchPrintWorksheetByWorksheetList(printWorksheetList, type, opt)
      this.setRecentPrintOption()
      this.resetPrintRangeCopyOptions()
      modalService.closeModal()
      globalLoaderService.closeLoader()
      toastSuccess('인쇄 요청에 성공했습니다.')
    } catch (error) {
      globalLoaderService.closeLoader()
      this.printErrorNotice(error)
    }
  }

  async onPrint(
    printTargetIds: number[],
    type?: WorksheetExportType,
    opt?: { order: WorksheetPrintOrder },
  ) {
    try {
      globalLoaderService.openLoader()
      await this.fetchPrintWorksheet(printTargetIds, type, opt)
      this.setRecentPrintOption()
      this.resetPrintRangeCopyOptions()
      modalService.closeModal()
      globalLoaderService.closeLoader()
      toastSuccess('인쇄 요청에 성공했습니다.')
    } catch (error) {
      globalLoaderService.closeLoader()
      this.printErrorNotice(error)
    }
  }

  // 최근 사용한 프린트 옵션 저장하기
  private setRecentPrintOption() {
    this.setPrintOption()
    localStorageService.setItem('RECENT_WORKSHEET_PRINT_UI_OPTION')(this.uiStore)
  }

  // setRecentPrintOption에서 저장하지 않는 매수, 범위 옵션 리셋
  private resetPrintRangeCopyOptions() {
    this.printOption.copyNumber = 1
    this.rangeOption.reset()
  }

  private async fetchWorksheetDetail(targetId: number, ignoredForDeleted?: boolean) {
    const { pdf } = await worksheetController.fetchWorksheetDetail(targetId, ignoredForDeleted)
    const printWorksheetInfo = new PrintPageInfo({
      initPage: 0,
      elementPage: pdf?.elementPage,
      answerSolutionPage: pdf?.answerSolutionPage,
      quickAnswerPage: pdf?.quickAnswerPage,
      omrPage: pdf?.omrPage,
      targetId,
    })
    return printWorksheetInfo
  }

  private fetchPrintWorksheetByWorksheetList = async (
    printWorksheetList: WorksheetPrintService['printWorksheetList'],
    type?: WorksheetExportType,
    opt?: { order?: WorksheetPrintOrder },
  ) => {
    const pieces = printWorksheetList.map(({ id, assignedStudentWorksheetList }) => {
      return {
        pieceId: id,
        names:
          this.uiStore.useStudentOption && this.uiStore.visibleStudentOption === 'VISIBLE'
            ? toJS(assignedStudentWorksheetList.map(({ studentName }) => studentName))
            : [],
        studentIds:
          this.uiStore.useStudentOption && this.uiStore.visibleStudentOption === 'VISIBLE'
            ? toJS(assignedStudentWorksheetList.map(({ studentId }) => studentId))
            : [],
        page: (() => {
          let page = ''
          const target = this.printPageInfoList?.find((pageInfo) => pageInfo.targetId === id)

          // 전체 출력
          if (this.uiStore.visibleRangeOption === 'ALL') {
            if (target) {
              const { initPage, elementPage, quickAnswerPage, answerSolutionPage, omrPage } = target
              const maxPage = Math.max(omrPage, elementPage, quickAnswerPage, answerSolutionPage)
              page = `${initPage || 1}-${maxPage}`
            } else {
              page = this.maximumPageRange
            }
          }

          // 하위 옵션이 선택되지 않은 - 사용자가 직접 입력
          else if (this.rangeOption.allNotSelect) {
            page = this.rangeOption.inputText
          }

          // 하위 옵션이 선택되면 - 각 학습지별 범위 계산
          else if (target) {
            page = this.getPage({
              initPage: target.initPage,
              elementPage: target.elementPage,
              quickAnswerPage: target.quickAnswerPage,
              answerSolutionPage: target.answerSolutionPage,
              omrPage: target.omrPage,
            })
          } else {
            page = this.rangeOption.inputText
          }

          return target ? this.getDividedProblemAndAnswerPage(page, [target?.elementPage]) : page
        })(),
      }
    })

    if (commonRepo.user?.teacherId) {
      const data: WorksheetPrintRequest = {
        ...this.printOption.params,
        pieces,
        teacherId: commonRepo.user.teacherId,
        rangeOption: {
          problem: this.rangeOption.problem,
          quickAnswer: this.rangeOption.quickAnswer,
          answer: this.rangeOption.answer,
          omr: this.rangeOption.omr,
        },
      }

      if (opt?.order !== 'ignore-order') {
        data.order = this.order
      }

      await printController.postMQTTPrintWorksheet(data, type)
    }
  }

  private fetchPrintWorksheet = async (
    printWorksheetIds: number[],
    type?: WorksheetExportType,
    opt?: { order?: WorksheetPrintOrder },
  ) => {
    const pieces = printWorksheetIds.map((worksheetId) => {
      return {
        pieceId: worksheetId,
        names:
          this.uiStore.useStudentOption && this.uiStore.visibleStudentOption === 'VISIBLE'
            ? toJS(this.studentNameList)
            : [],
        studentIds:
          this.uiStore.useStudentOption && this.uiStore.visibleStudentOption === 'VISIBLE'
            ? toJS(this.studentIdList)
            : [],
        page: (() => {
          let page = ''
          const target = this.printPageInfoList?.find(
            (pageInfo) => pageInfo.targetId === worksheetId,
          )

          // 전체 출력
          if (this.uiStore.visibleRangeOption === 'ALL') {
            if (target) {
              const { initPage, elementPage, quickAnswerPage, answerSolutionPage, omrPage } = target
              const maxPage = Math.max(omrPage, elementPage, quickAnswerPage, answerSolutionPage)
              page = `${initPage || 1}-${maxPage}`
            } else {
              page = this.maximumPageRange
            }
          }

          // 하위 옵션이 선택되지 않은 - 사용자가 직접 입력
          else if (this.rangeOption.allNotSelect) {
            page = this.rangeOption.inputText
          }

          // 하위 옵션이 선택되면 - 각 학습지별 범위 계산
          else if (target) {
            page = this.getPage({
              initPage: target.initPage,
              elementPage: target.elementPage,
              quickAnswerPage: target.quickAnswerPage,
              answerSolutionPage: target.answerSolutionPage,
              omrPage: target.omrPage,
            })
          } else {
            page = this.rangeOption.inputText
          }

          return target ? this.getDividedProblemAndAnswerPage(page, [target?.elementPage]) : page
        })(),
      }
    })

    if (commonRepo.user?.teacherId) {
      const data: WorksheetPrintRequest = {
        ...this.printOption.params,
        pieces,
        teacherId: commonRepo.user.teacherId,
        rangeOption: {
          problem: this.rangeOption.problem,
          quickAnswer: this.rangeOption.quickAnswer,
          answer: this.rangeOption.answer,
          omr: this.rangeOption.omr,
        },
      }

      if (opt?.order !== 'ignore-order') {
        data.order = this.order
      }

      await printController.postMQTTPrintWorksheet(data, type)
    }
  }

  private getPage({ initPage, elementPage, quickAnswerPage, answerSolutionPage, omrPage }) {
    // 범위 계산
    // 1 ~ elemntPage : 문제 범위
    // elementPage + 1 ~ quickAnswerPage : 빠른정답 범위
    // quickAnswerPage + 1  ~ answerSolutionPage : 정답과 해설 범위
    // answerSolutionPage + 1 ~ omrPage : omr 범위
    // 결과  1-10 ,  1-10,15-20 이런식으로 나옴

    if (this.rangeOption.allSelect) {
      return this.maximumPageRange
    }

    const range = {
      problem: (() => {
        if (initPage === 0 && elementPage === 1) return '1'

        return initPage === elementPage ? `${elementPage}` : `${initPage}-${elementPage}`
      })(),
      quickAnswer: (() => {
        if (!quickAnswerPage) return ''
        if (!elementPage || elementPage + 1 === quickAnswerPage) return `${quickAnswerPage}`

        return `${elementPage + 1}-${quickAnswerPage}`
      })(),
      answer: (() => {
        if (!answerSolutionPage) return ''
        if (!quickAnswerPage || quickAnswerPage + 1 === answerSolutionPage)
          return `${answerSolutionPage}`
        return `${quickAnswerPage + 1}-${answerSolutionPage}`
      })(),
      omr: (() => {
        if (!omrPage) return ''
        if (!answerSolutionPage || answerSolutionPage + 1 === omrPage) return `${omrPage}`
        return `${answerSolutionPage + 1}-${omrPage}`
      })(),
    }

    let text = ''

    for (const option in range) {
      if (this.rangeOption[option] && range[option] !== '') {
        text += `${range[option]},`
      }
    }

    const result = text.slice(0, text.length - 1) //  마지막 쉼표 제거
    return result
  }

  printError(error: Error) {
    return this.printErrorNotice(error)
  }
}

class UIStore extends DTO {
  visibleRangeOption: 'ALL' | 'SELECT' = 'SELECT'
  visibleStudentOption: 'NONE' | 'VISIBLE' = 'VISIBLE'
  useStudentOption = true

  constructor() {
    super()

    makeObservable(this, {
      visibleRangeOption: observable,
      visibleStudentOption: observable,
      useStudentOption: observable,
    })
  }
}

export const worksheetPrintService = new WorksheetPrintService()
